import React, { useState, useEffect } from 'react';
import './App.css';
import Layout from './components/LayoutNew';
import AstroDetailsForm from './Pages/AstroDetailsForm';
import AstroWelcome from './Pages/AstroWelcome';
import Chat from './Pages/Chat';
import Signup from './Pages/Signup';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import TokenPack from './Pages/TokenPack';
import { ChatProvider } from './context/ChatContext';
import IdeaWhatToAsk from './Pages/IdeaWhatToAsk';
import SignupForm from './Pages/SignupForm';
import Login from './Pages/Login';
import Settings from './Pages/Setting';
import ChartDetails from './Pages/ChartDetails';
import Verify from './Pages/Verify';
import ForgetPassword from './Pages/ForgetPassword';
import ResetPassword from './Pages/ResetPassword';

import RequireVisitedDetail from './components/RequiredVisitedDetail';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import Home from './Pages/Home/Home';
import { useAuth } from './context/AuthContext';
import { Analytics } from '@vercel/analytics/react';
import { PopupProvider } from './context/PopupContext';

function App() {
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('prefersDarkMode')
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('prefersDarkMode');
      if (currentToken == null) setDarkMode(null);
      else if (darkMode !== currentToken) setDarkMode(currentToken);
    }, 1000); // Poll every second
    return () => clearInterval(interval);
  }, [darkMode]);

  const systemPreference = useMediaQuery('(prefers-color-scheme: dark)');
  const [prefersDarkMode, setPrefersDarkMode] = useState(systemPreference);

  useEffect(() => {
    if (darkMode != null) setPrefersDarkMode(darkMode === 'true');
  }, [darkMode]);

  useEffect(() => {
    if (darkMode == null) setPrefersDarkMode(systemPreference);
  }, [systemPreference, darkMode]);

  console.log('Prefers Dark Mode:', prefersDarkMode.toString());

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      h1: {
        fontSize: 'clamp(28px, 4.5vw, 60px)',
        fontWeight: 500,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      h2: {
        fontSize: 'clamp(22px, 3.2vw, 34px)',
        fontWeight: 500,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      h3: {
        fontSize: 'clamp(18px, 2.4vw, 24px)',
        fontWeight: 500,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      h4: {
        fontSize: 'clamp(16px, 1.8vw, 18px)',
        fontWeight: 400,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      h5: {
        fontSize: 'clamp(8px, 1.2vw, 14px)',
        fontWeight: 400,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      h6: {
        fontSize: 'clamp(6px, 0.6vw, 12px)',
        fontWeight: 400,
        fontFamily: `Inter, sans-serif`,
        color: prefersDarkMode ? '#bababa' : '#454545',
      },
      homePageBody: {
        fontFamily: `Inter, sans-serif`,
        fontSize: 16,
        color: prefersDarkMode ? '#bababa' : '#454545',
        fontWeight: 500,
        lineHeight: '150%',
      },
      allVariants: {
        fontFamily: `Inter, sans-serif`,
        fontSize: 16,
        color: prefersDarkMode ? '#bababa' : '#454545',
        fontWeight: 500,
        lineHeight: '150%',
      },
    },
    palette: prefersDarkMode
      ? {
          mode: prefersDarkMode ? 'dark' : 'light',
          primary: {
            main: '#000000',
          },
          background: {
            paper: '#121212',
            default: '#1e1e1e',
          },
          text: {
            primary: '#bababa',
          },
        }
      : {
          mode: prefersDarkMode ? 'dark' : 'light',
          primary: {
            main: '#ffffff',
          },
          background: {
            paper: '#f5f5f5',
            default: '#ffffff',
          },
          text: {
            primary: '#000000',
          },
        },
    components: {
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            display: 'none',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          '*, *::before, *::after': {
            /* Firefox */
            scrollbarWidth: 'thin',
            scrollbarColor: prefersDarkMode
              ? 'rgba(255,255,255,0.4) transparent'
              : 'rgba(0,0,0,0.4) transparent',
          },

          /* WebKit (Chrome, Safari, Edge) */
          '*::-webkit-scrollbar': {
            width: '4px', // small, thin scrollbar
            height: '4px',
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: prefersDarkMode
              ? 'rgba(255,255,255,0.4)'
              : 'rgba(0,0,0,0.4)',
            borderRadius: '0px',
            width: '4px', // small, thin scrollbar
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: prefersDarkMode
              ? 'rgba(255,255,255,0.4)'
              : 'rgba(0,0,0,0.4)',
            width: '4px', // small, thin scrollbar
            borderRadius: '1px',
          },
          '*::-webkit-scrollbar-thumb:active': {
            backgroundColor: prefersDarkMode
              ? 'rgba(255,255,255,0.4)'
              : 'rgba(0,0,0,0.4)',
            width: '4px', // small, thin scrollbar
            borderRadius: '1px',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            marginTop: '50px',
            marginBottom: '50px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: ({ theme }) => ({
            marginTop: '20px',
            marginBottom: '20px',
            [theme.breakpoints.up('sm')]: {
              marginTop: '30px',
              marginBottom: '30px',
            },
            [theme.breakpoints.up('md')]: {
              marginTop: '50px',
              marginBottom: '46px',
            },
            [theme.breakpoints.up('lg')]: {
              marginTop: '50px',
              marginBottom: '46px',
            },
          }),
          h2: ({ theme }) => ({
            marginBottom: '16px',
            [theme.breakpoints.up('sm')]: {
              marginBottom: '26px',
            },
            [theme.breakpoints.up('md')]: {
              marginBottom: '36px',
            },
            [theme.breakpoints.up('lg')]: {
              marginBottom: '36px',
            },
          }),
          h3: ({ theme }) => ({
            marginBottom: '16px',
            [theme.breakpoints.up('sm')]: {
              marginBottom: '26px',
            },
            [theme.breakpoints.up('md')]: {
              marginBottom: '36px',
            },
            [theme.breakpoints.up('lg')]: {
              marginBottom: '36px',
            },
          }),
          homePageBody: ({ theme }) => ({
            marginBottom: '16px',
            [theme.breakpoints.up('sm')]: {
              marginBottom: '26px',
            },
            [theme.breakpoints.up('md')]: {
              marginBottom: '36px',
            },
            [theme.breakpoints.up('lg')]: {
              marginBottom: '36px',
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: prefersDarkMode ? '#000000' : '#ffffff',
            color: prefersDarkMode ? '#bababa' : '#454545',
          },
          containedSecondary: {
            backgroundColor: prefersDarkMode ? '#bababa' : '#252525',
            textTransform: 'none',
            color: prefersDarkMode ? '#000000' : '#ffffff',
            borderRadius: '69px',
            fontWeight: 700,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            color:
              ownerState.color === 'secondary'
                ? prefersDarkMode
                  ? '#bababa'
                  : '#454545'
                : prefersDarkMode
                  ? '#90caf9'
                  : '#1976d2', // set default color
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: prefersDarkMode ? '#bbdefb' : '#115293',
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: prefersDarkMode ? '#ffffff' : '#777777', // change to your desired focus color
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: prefersDarkMode ? '#ffffff' : '#777777', // change label color when focused
            },
          },
        },
      },
    },
  });

  const { isLogin } = useAuth();

  return (
    <ChatProvider>
      <PopupProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Analytics />
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              {!isLogin ? (
                <>
                  <Route path="/" element={<Navigate to="/chat" />} />
                  <Route path="/home/:tab/:subTab?" element={<Home />} />
                  <Route path="/signin" element={<Signup />} />
                  <Route path="/signup" element={<SignupForm />} />
                  <Route path="/forget-password" element={<ForgetPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/detail" element={<AstroDetailsForm />} />
                  <Route path="/verify" element={<Verify />} />
                  <Route path="/what-to-ask" element={<IdeaWhatToAsk />} />
                  <Route path="/chat" element={<Layout />} />
                  {/* Redirect all other paths to login */}
                  {/* <Route path="*" element={<Navigate to="/login" replace />} /> */}
                </>
              ) : (
                <>
                  {/* Protected Routes */}
                  <Route path="/verify" element={<Verify />} />
                  <Route path="/signup" element={<SignupForm />} />
                  <Route path="/welcome" element={<AstroWelcome />} />
                  <Route path="/detail" element={<AstroDetailsForm />} />
                  <Route path="/pricing" element={<TokenPack />} />
                  <Route path="/what-to-ask" element={<IdeaWhatToAsk />} />
                  <Route path="/my-settings" element={<Settings />} />
                  <Route path="/chart-detail" element={<ChartDetails />} />
                  <Route path="/home/:tab/:subTab?" element={<Home />} />
                  <Route
                    path="/chat"
                    element={
                      <RequireVisitedDetail>
                        <Layout />
                      </RequireVisitedDetail>
                    }
                  />
                  {/* Redirect all other paths to /chat */}
                  <Route path="*" element={<Navigate to="/detail" replace />} />
                </>
              )}
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </PopupProvider>
    </ChatProvider>
  );
}

export default App;
